import React, { createContext, useState, useContext, ReactNode } from 'react';

type Language = 'ru' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  ru: {
    // Общие строки
    'site.language': 'Язык',
    
    // Заголовки страниц
    'pages.home.title': 'Главная',
    'pages.profile.title': 'Профиль',
    'pages.tasks.title': 'Задания',
    
    // Главная страница - SectionInformation
    'home.title': 'Gramster',
    'home.description': 'Грамстер будет анонимно следить за появлением историй в профилях Instagram и сохранять их сразу после публикации, чтобы вы могли отслеживать удаленные моменты своих друзей.',
    'home.input.placeholder': '@username или https://instagram.com/...',
    'home.input.hint': '* Только открытые аккаунты Instagram',
    'home.notification.success': 'Успешно создано',
    'home.notification.error': 'Введите верный @username или ссылку на Instagram',
    
    // Секция заданий
    'tasks.title': 'Подпишись на всех и получи бесплатный доступ к сервису',
    'tasks.reward.title': 'Ваша награда',
    'tasks.reward.days': '30 дней',
    'tasks.reward.service': 'бесплатного доступа',
    'tasks.reward.subtitle': 'за выполнение всех обычных заданий',
    'tasks.reward.partners': 'Партнерские задания (фиолетовые) дают дополнительные дни',
    'tasks.warning': 'Важно: если вы отпишетесь от каналов после получения бонуса, ваш бесплатный доступ будет аннулирован.',
    
    // Компонент TaskCard
    'taskcard.completed': 'Выполнено',
    'taskcard.partner_days': '+2 дня',
    
    // Задания
    'task.1.title': 'Подписаться на канал @gramster_news',
    'task.1.description': 'Официальный канал с новостями сервиса',
    'task.1.button': 'Подписаться',
    
    'task.2.title': 'Подписаться на канал @gramster_updates',
    'task.2.description': 'Канал с обновлениями функционала сервиса',
    'task.2.button': 'Подписаться',
    
    'task.3.title': 'Запустить бота @gramster_support_bot',
    'task.3.description': 'Бот технической поддержки сервиса',
    'task.3.button': 'Запустить',
    
    'task.4.title': 'Подписаться на канал @gramster_examples',
    'task.4.description': 'Примеры использования сервиса',
    'task.4.button': 'Подписаться',
    
    'task.5.title': 'Партнер',
    'task.5.description': 'Выполните партнерское задание и получите +2 дня доступа к сервису',
    'task.5.button': 'Выполнить',
    
    // Меню и навигация
    'nav.home': 'Главная',
    'nav.profile': 'Профиль',
    'nav.tasks': 'Задания',
    'nav.settings': 'Настройки',
    'nav.create_tracking': 'Создать отслеживание',
    
    // Профиль
    'profile.remaining': 'Осталось',
    'profile.count': '1 профиль',
  },
  en: {
    // Общие строки
    'site.language': 'Language',
    
    // Заголовки страниц
    'pages.home.title': 'Home',
    'pages.profile.title': 'Profile',
    'pages.tasks.title': 'Tasks',
    
    // Главная страница - SectionInformation
    'home.title': 'Gramster',
    'home.description': 'Gramster will anonymously monitor Instagram profiles for new stories and save them immediately after publication, allowing you to track deleted moments of your friends.',
    'home.input.placeholder': '@username or https://instagram.com/...',
    'home.input.hint': '* Public Instagram accounts only',
    'home.notification.success': 'Successfully created',
    'home.notification.error': 'Please enter a valid @username or Instagram link',
    
    // Секция заданий
    'tasks.title': 'Subscribe to all and get free service access',
    'tasks.reward.title': 'Your reward',
    'tasks.reward.days': '30 days',
    'tasks.reward.service': 'of free access',
    'tasks.reward.subtitle': 'for completing all regular tasks',
    'tasks.reward.partners': 'Partner tasks (purple) give additional days',
    'tasks.warning': 'Important: if you unsubscribe from channels after receiving the bonus, your free access will be revoked.',
    
    // Компонент TaskCard
    'taskcard.completed': 'Completed',
    'taskcard.partner_days': '+2 days',
    
    // Задания
    'task.1.title': 'Subscribe to @gramster_news channel',
    'task.1.description': 'Official channel with service news',
    'task.1.button': 'Subscribe',
    
    'task.2.title': 'Subscribe to @gramster_updates channel',
    'task.2.description': 'Channel with service functionality updates',
    'task.2.button': 'Subscribe',
    
    'task.3.title': 'Start @gramster_support_bot',
    'task.3.description': 'Service technical support bot',
    'task.3.button': 'Start',
    
    'task.4.title': 'Subscribe to @gramster_examples channel',
    'task.4.description': 'Service usage examples',
    'task.4.button': 'Subscribe',
    
    'task.5.title': 'Partner',
    'task.5.description': 'Complete the partner task and get +2 days of service access',
    'task.5.button': 'Complete',
    
    // Меню и навигация
    'nav.home': 'Home',
    'nav.profile': 'Profile',
    'nav.tasks': 'Tasks',
    'nav.settings': 'Settings',
    'nav.create_tracking': 'Create tracking',
    
    // Профиль
    'profile.remaining': 'Remaining',
    'profile.count': '1 profile',
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('ru');

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations[typeof language]] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 